// Tempo helpers — read/write the song's tempo in BPM.
//
// Strudel's native unit is cycles per minute (`setcpm`) or cycles per
// second (`setcps`). Musicians think in BPM. One cycle = four beats,
// so BPM = cpm × 4 = cps × 60 × 4. The seeds in defaults.ts use the
// `setcpm(N / 4)` convention to make BPM legible in the code; we
// preserve that on writes.
//
// setTempo always produces a `setcpm(bpm / 4)` binary expression —
// replaces any existing setcpm / setcps statement, inserts at the top
// of the song if none exists. Replacing setcps with setcpm on update
// keeps the code homogeneous; the user hasn't lost meaning because
// both express the same underlying rate.

import { produce } from 'immer';
import { newBlockId } from './types';
import type { Numeric, Song, Statement } from './types';

/** BPM value of the first setcpm/setcps statement in the song, or
 *  `null` if none exists or the numeric can't be evaluated
 *  statically (e.g. references a ref or an unsupported modulator). */
export function extractTempo(song: Song): number | null {
  for (const stmt of song.statements) {
    if (stmt.kind === 'setcpm') {
      const n = evalNumeric(stmt.value);
      if (n === null) return null;
      return n * 4;
    }
    if (stmt.kind === 'setcps') {
      const n = evalNumeric(stmt.value);
      if (n === null) return null;
      return n * 60 * 4;
    }
  }
  return null;
}

/** Return a new Song with tempo set to `bpm`. Preserves block ids of
 *  unaffected statements so the UI's solo / target state survives
 *  the transform. */
export function setTempo(song: Song, bpm: number): Song {
  const clamped = clampBpm(bpm);
  return produce(song, (draft) => {
    const existingIdx = draft.statements.findIndex(
      (s) => s.kind === 'setcpm' || s.kind === 'setcps',
    );

    if (existingIdx >= 0) {
      const existing = draft.statements[existingIdx]!;
      // Replace in place with a setcpm(bpm/4) statement, keeping the
      // original id so any UI selection / scroll state survives.
      draft.statements[existingIdx] = {
        ...existing,
        kind: 'setcpm',
        value: bpmOverFour(clamped),
      };
      return;
    }

    // No existing tempo statement — insert at the top but after any
    // leading comments so the tempo sits inside the code block proper.
    const insertAt = findInsertionIndex(draft.statements);
    const newStmt: Statement = {
      id: newBlockId(),
      kind: 'setcpm',
      value: bpmOverFour(clamped),
    };
    draft.statements.splice(insertAt, 0, newStmt);
  });
}

// ─── Internals ──────────────────────────────────────────────────────

const MIN_BPM = 20;
const MAX_BPM = 400;

function clampBpm(bpm: number): number {
  if (!Number.isFinite(bpm)) return 120;
  // Round to the nearest integer — sub-BPM precision buys nothing
  // musically and clutters the rendered source.
  return Math.max(MIN_BPM, Math.min(MAX_BPM, Math.round(bpm)));
}

function bpmOverFour(bpm: number): Numeric {
  return {
    kind: 'binary',
    op: '/',
    lhs: { kind: 'num', value: bpm },
    rhs: { kind: 'num', value: 4 },
  };
}

// A minimal numeric evaluator for pure-number expressions. Covers the
// common cases: literal `setcpm(30)`, binary `setcpm(120 / 4)`,
// nested literals. Returns null for anything involving refs, modulators,
// or raw source — those shapes can't be reduced statically.
function evalNumeric(n: Numeric): number | null {
  if (n.kind === 'num') return n.value;
  if (n.kind === 'binary') {
    const l = evalNumeric(n.lhs);
    const r = evalNumeric(n.rhs);
    if (l === null || r === null) return null;
    if (n.op === '+') return l + r;
    if (n.op === '-') return l - r;
    if (n.op === '*') return l * r;
    if (n.op === '/') return r === 0 ? null : l / r;
  }
  return null;
}

// Skip leading comments so the inserted tempo sits next to the first
// real statement rather than orphaned above a header comment.
function findInsertionIndex(stmts: Statement[]): number {
  for (let i = 0; i < stmts.length; i++) {
    if (stmts[i]!.kind !== 'comment') return i;
  }
  return stmts.length;
}
